import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import {
  addNativeResultListener,
  getStandContext,
  getStandDetectionStatus,
  isNativeApp,
  setStandAutoApply,
  setStandOverride,
} from "@/lib/hiveflowNative";

const NativeStandContext = createContext(null);

const CONTEXT_EVENT_TYPES = new Set([
  "STAND_CONTEXT_ACTIVE",
  "STAND_CONTEXT_CHANGED",
  "STAND_CONTEXT_ENTERED",
  "STAND_CONTEXT_RESULT",
  "STAND_CONTEXT_SUGGESTION",
  "STAND_CONTEXT_UNAVAILABLE",
  "STAND_CONTEXT_EXITED",
]);

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== "") ?? null;
}

function nestedContexts(payload = {}) {
  return [payload, payload.context, payload.standContext, payload.stand_context].filter(Boolean);
}

export function standStateFromPayload(payload = {}) {
  const value = nestedContexts(payload).map((item) => firstValue(item.state, item.contextState, item.context_state)).find(Boolean);
  return String(value || "").toUpperCase();
}

export function standIdFromPayload(payload = {}, kind = "stand") {
  const contexts = nestedContexts(payload);
  const keys = kind === "suggested"
    ? ["suggestedStandId", "suggested_stand_id"]
    : kind === "override"
      ? ["overrideStandId", "override_stand_id"]
      : ["activeStandId", "active_stand_id", "standId", "stand_id", "apiaryId", "apiary_id"];

  for (const item of contexts) {
    const direct = firstValue(...keys.map((key) => item?.[key]));
    if (direct) return String(direct);
    const stand = kind === "suggested" ? item?.suggestedStand : kind === "override" ? item?.overrideStand : item?.activeStand || item?.stand;
    const nested = firstValue(stand?.standId, stand?.stand_id, stand?.apiaryId, stand?.apiary_id, stand?.id);
    if (nested) return String(nested);
  }
  return null;
}

export function standNameFromPayload(payload = {}, kind = "stand") {
  const contexts = nestedContexts(payload);
  const keys = kind === "suggested"
    ? ["suggestedStandName", "suggested_stand_name", "standName", "stand_name", "apiaryName", "apiary_name"]
    : ["activeStandName", "active_stand_name", "standName", "stand_name", "apiaryName", "apiary_name"];

  for (const item of contexts) {
    const direct = firstValue(...keys.map((key) => item?.[key]));
    if (direct) return String(direct);
    const stand = kind === "suggested" ? item?.suggestedStand || item?.stand : item?.activeStand || item?.stand;
    if (stand?.name) return String(stand.name);
  }
  return "";
}

function numberFromPayload(payload = {}, ...keys) {
  for (const item of nestedContexts(payload)) {
    for (const key of keys) {
      const value = item?.[key];
      if (value === undefined || value === null || value === "") continue;
      const number = Number(value);
      if (Number.isFinite(number)) return number;
    }
  }
  return null;
}

function eventTime(value) {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number") return value < 1e12 ? value * 1000 : value;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric < 1e12 ? numeric * 1000 : numeric;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function NativeStandProvider({ children }) {
  const location = useLocation();
  const nativeApp = isNativeApp();
  const [apiaries, setApiaries] = useState([]);
  const apiariesRef = useRef([]);
  const [detectionStatus, setDetectionStatus] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  const [activeContext, setActiveContext] = useState(null);
  const [dismissedStandId, setDismissedStandId] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const seenEventIds = useRef(new Set());
  const latestContextTime = useRef(0);

  useEffect(() => {
    if (!nativeApp) return undefined;
    let cancelled = false;
    api.get("/apiaries")
      .then((response) => {
        if (cancelled) return;
        const next = response.data || [];
        apiariesRef.current = next;
        setApiaries(next);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [nativeApp]);

  const standById = useCallback((standId) => (
    apiariesRef.current.find((stand) => String(stand.id) === String(standId)) || null
  ), []);

  const applyContextEvent = useCallback((result) => {
    const { type, payload = {}, eventId, timestamp } = result;
    if (eventId) {
      if (seenEventIds.current.has(eventId)) return;
      seenEventIds.current.add(eventId);
      if (seenEventIds.current.size > 100) {
        seenEventIds.current.delete(seenEventIds.current.values().next().value);
      }
    }

    if (CONTEXT_EVENT_TYPES.has(type)) {
      const receivedAt = eventTime(timestamp);
      if (receivedAt && receivedAt < latestContextTime.current) return;
      if (receivedAt) latestContextTime.current = receivedAt;
    }

    if (type === "STAND_DETECTION_STATUS_RESULT") {
      setDetectionStatus(payload);
    }

    if (type === "STAND_CONTEXT_EXITED" || type === "STAND_CONTEXT_UNAVAILABLE") {
      setSuggestion(null);
      setActiveContext(null);
      setDismissedStandId(null);
      setConfirming(false);
      return;
    }

    if (!CONTEXT_EVENT_TYPES.has(type) && type !== "STAND_DETECTION_STATUS_RESULT") return;

    const explicitState = standStateFromPayload(payload);
    const explicitSuggestedStandId = standIdFromPayload(payload, "suggested");
    const genericStandId = standIdFromPayload(payload);
    const overrideStandId = standIdFromPayload(payload, "override");
    const state = explicitState
      || (type === "STAND_CONTEXT_SUGGESTION" || explicitSuggestedStandId ? "SUGGESTED" : "")
      || (overrideStandId || genericStandId ? "ACTIVE" : "");
    const suggestedStandId = explicitSuggestedStandId || (state === "SUGGESTED" ? genericStandId : null);
    const activeStandId = overrideStandId || genericStandId;

    if (state === "SUGGESTED" || type === "STAND_CONTEXT_SUGGESTION") {
      if (!suggestedStandId) return;
      const knownStand = standById(suggestedStandId);
      setSuggestion({
        ...payload,
        state: "SUGGESTED",
        suggestedStandId,
        standName: standNameFromPayload(payload, "suggested") || knownStand?.name || "",
        distanceMeters: numberFromPayload(payload, "distanceMeters", "distance_meters"),
        accuracyMeters: numberFromPayload(payload, "accuracyMeters", "accuracy_meters", "horizontalAccuracy", "horizontal_accuracy"),
        confidence: firstValue(payload.confidence, payload.context?.confidence, payload.standContext?.confidence),
        eventId: eventId || null,
      });
      setConfirming(false);
      return;
    }

    if ((state === "ACTIVE" || state === "OVERRIDDEN" || type === "STAND_CONTEXT_ACTIVE" || type === "STAND_CONTEXT_ENTERED" || type === "STAND_CONTEXT_CHANGED") && activeStandId) {
      const knownStand = standById(activeStandId);
      setActiveContext({
        ...payload,
        state: state || "ACTIVE",
        activeStandId,
        standName: standNameFromPayload(payload) || knownStand?.name || "",
        eventId: eventId || null,
      });
      setSuggestion(null);
      setDismissedStandId(null);
      setConfirming(false);
    }
  }, [standById]);

  const requestStandContext = useCallback(() => {
    if (!nativeApp) return false;
    getStandDetectionStatus();
    return getStandContext();
  }, [nativeApp]);

  useEffect(() => {
    if (!nativeApp) return undefined;
    const stop = addNativeResultListener(applyContextEvent);
    getStandDetectionStatus();
    getStandContext();
    return stop;
  }, [applyContextEvent, nativeApp]);

  useEffect(() => {
    if (!nativeApp) return undefined;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") requestStandContext();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [nativeApp, requestStandContext]);

  useEffect(() => {
    if (nativeApp && location.pathname === "/inspectie/nieuw") requestStandContext();
  }, [location.key, location.pathname, nativeApp, requestStandContext]);

  const acceptSuggestion = useCallback(() => {
    if (!suggestion?.suggestedStandId) return false;
    setConfirming(true);
    const sent = setStandOverride(suggestion.suggestedStandId);
    if (!sent) setConfirming(false);
    return sent;
  }, [suggestion]);

  const dismissSuggestion = useCallback(() => {
    if (suggestion?.suggestedStandId) setDismissedStandId(suggestion.suggestedStandId);
  }, [suggestion]);

  const overrideStand = useCallback((standId) => {
    if (!standId || !nativeApp) return false;
    setConfirming(true);
    const sent = setStandOverride(standId);
    if (!sent) setConfirming(false);
    return sent;
  }, [nativeApp]);

  const updateAutoApply = useCallback((enabled) => {
    if (!nativeApp) return false;
    const sent = setStandAutoApply(enabled);
    if (sent) {
      setDetectionStatus((current) => ({ ...(current || {}), autoApplyEnabled: Boolean(enabled) }));
    }
    return sent;
  }, [nativeApp]);

  useEffect(() => {
    if (!confirming) return undefined;
    const timeout = setTimeout(() => {
      setConfirming(false);
      requestStandContext();
    }, 8000);
    return () => clearTimeout(timeout);
  }, [confirming, requestStandContext]);

  const value = useMemo(() => {
    const suggestedStand = suggestion?.suggestedStandId
      ? apiaries.find((stand) => String(stand.id) === String(suggestion.suggestedStandId)) || null
      : null;
    const activeStand = activeContext?.activeStandId
      ? apiaries.find((stand) => String(stand.id) === String(activeContext.activeStandId)) || null
      : null;
    return {
      nativeApp,
      apiaries,
      detectionStatus,
      suggestion: suggestion ? { ...suggestion, standName: suggestion.standName || suggestedStand?.name || "Deze bijenstand" } : null,
      showSuggestion: Boolean(suggestion && suggestion.suggestedStandId !== dismissedStandId),
      confirming,
      activeContext: activeContext ? { ...activeContext, standName: activeContext.standName || activeStand?.name || "Actieve bijenstand" } : null,
      activeStandId: activeContext?.activeStandId || null,
      activeStand,
      autoApplyEnabled: Boolean(
        detectionStatus?.autoApplyEnabled
        ?? detectionStatus?.auto_apply_enabled
        ?? suggestion?.autoApplyEnabled
        ?? suggestion?.auto_apply_enabled
        ?? false
      ),
      acceptSuggestion,
      dismissSuggestion,
      overrideStand,
      updateAutoApply,
      requestStandContext,
    };
  }, [acceptSuggestion, activeContext, apiaries, confirming, detectionStatus, dismissSuggestion, dismissedStandId, nativeApp, overrideStand, requestStandContext, suggestion, updateAutoApply]);

  return <NativeStandContext.Provider value={value}>{children}</NativeStandContext.Provider>;
}

export function useNativeStand() {
  const context = useContext(NativeStandContext);
  if (!context) throw new Error("useNativeStand moet binnen NativeStandProvider worden gebruikt");
  return context;
}
